/**
 * Turns what the endpoints said about one firm into the viewer-scoped facts the Overview renders.
 * Pure: every rule here is a claim about what a backend returns to a given viewer, so each one
 * names the response it reads.
 */
import type { FirmDataOutcomes } from './FirmData';
import type { BillingResponse, BillingRule } from '@thinkalpha/locates-ws-client';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { FirmFacts, FirmRecord, MemberRecord } from 'src/admin/firms/FirmOverview/source';
import type { Route } from 'src/admin/LAB/contracts/routes';
import { grantedFirmIds } from 'src/admin/lib/permissions';
import { settle } from 'src/admin/lib/queryOutcome';
import type { Outcome } from 'src/admin/lib/queryOutcome';
import {
    toAccountLite,
    toBillingRuleLite,
    toFirmRecord,
    toMemberRecord,
    toPoolLite,
    toProviderLite,
    toRouteLite,
} from 'src/admin/lib/sourceRecords';
import { isRestricted, restricted } from 'src/admin/lib/visible';
import type { Visible } from 'src/admin/lib/visible';
import type { ViewerStanding } from 'src/admin/models/AdminViewerScope/ViewerStanding';

export interface AssemblyContext {
    standing: ViewerStanding;
    /**
     * Superuser perspective only: the unfiltered `GET /v1/routes` and `GET /v1/billing`, which are
     * the only REST answers to "routes granted to this firm" and "rules applied to this firm".
     */
    superuserLists: { routes: Outcome<Route[]>; billing: Outcome<BillingResponse> } | undefined;
    /** What `GET /v1/billing/firm/:id` said for each firm fetched, so parents' rules can be read for a child. */
    billingOwnedByFirm: ReadonlyMap<string, Outcome<BillingResponse>>;
    /** Every membership in a fetched member list, to name the firm a dependent membership depends on. */
    membershipsById: ReadonlyMap<string, FirmMembershipWithProfile>;
    /** Firms already known, so untyped permission entries can be recognised as firms. */
    knownFirmIds: ReadonlySet<string>;
}

export type FirmAssembly =
    | { status: 'pending' }
    | { status: 'failed'; error: Error }
    | { status: 'ready'; facts: Visible<FirmFacts>; members: Visible<MemberRecord[]> };

const ready = (facts: Visible<FirmFacts>, members: Visible<MemberRecord[]>): FirmAssembly => ({
    status: 'ready',
    facts,
    members,
});

/**
 * Facts for one firm as this viewer may know them. `GET /firms/:id` refusing the viewer (403 or
 * 404) makes the whole firm restricted: it exists, and that is all the viewer may know.
 */
export function assembleFirm(outcomes: FirmDataOutcomes, context: AssemblyContext): FirmAssembly {
    const opened = settle({ firm: outcomes.firm });
    if (opened.status !== 'settled') return opened;
    const firmOutcome = opened.outcomes.firm;
    if (firmOutcome.status === 'refused') return ready(restricted('other-firm'), restricted('other-firm'));

    const { standing } = context;
    // Resource requests settle independently. One that fails for a reason other than access only
    // takes its own field down, as "unavailable"; a firm is never failed by one of its resources.
    // UAT showed why: a member list 500s when one Keycloak profile lacks a name, and the rest of
    // the firm is still worth showing.
    // A request still in flight marks its field as loading, so the page renders around it.
    const { members, accounts, risk, providers, routes, pools, billing } = outcomes;

    const firm = toFirmRecord(firmOutcome.data);
    const id = firm.id;
    const denied = restricted(standing.refusalReason(id));
    /** Refused means the viewer may not see it; failed means nobody could read it just now. */
    const withheld = (outcome: Outcome<unknown>) =>
        outcome.status === 'failed'
            ? restricted('unavailable')
            : outcome.status === 'pending'
              ? restricted('loading')
              : denied;
    const { knownFirmIds } = context;
    const isSuperuser = standing.isSuperuser;
    const ownsFirm = standing.hasRung(id, 'owner');
    const readsFirm = standing.hasRung(id, 'reader');

    const memberRecords: Visible<MemberRecord[]> =
        members.status === 'ok'
            ? members.data.map((membership) => toMemberRecord(membership, context.membershipsById))
            : withheld(members);

    // The list endpoint answers an unreadable firm with an empty list, never a refusal, so an empty
    // list from a viewer with no rung on the firm is "hidden", not "none".
    const accountsVisible: FirmFacts['accounts'] =
        accounts.status === 'ok' && (accounts.data.length > 0 || readsFirm)
            ? {
                  visible: accounts.data.map(toAccountLite),
                  total: isSuperuser ? accounts.data.length : null,
              }
            : withheld(accounts);

    const labEnrollment: FirmFacts['labEnrollment'] = risk.status === 'ok' ? 'unknown' : withheld(risk);

    // A refused request must not read as zero providers, so it carries no count at all.
    const ownedProviders = providers.status === 'ok' ? providers.data.filter((p) => p.firmId === id) : undefined;
    const providersOwned: FirmFacts['providersOwned'] = ownedProviders
        ? {
              visible: ownedProviders.map((p) => toProviderLite(p, knownFirmIds)),
              // locates-ws lists can omit rows even for superusers, so no caller gets an exact total here.
              total: null,
          }
        : { visible: [], total: null };

    const ownedRoutes = routes.status === 'ok' ? routes.data.filter((r) => r.firmId === id) : [];
    const routesOwned: FirmFacts['routesOwned'] =
        routes.status === 'ok' && (ownedRoutes.length > 0 || ownsFirm)
            ? {
                  visible: ownedRoutes.map((r) => toRouteLite(r, knownFirmIds)),
                  // locates-ws lists can omit rows even for owners, so there is no exact total here.
                  total: null,
              }
            : withheld(routes);

    const ownedPools = pools.status === 'ok' ? pools.data.pools.filter((p) => p.firmId === id) : [];
    const poolsVisible: FirmFacts['pools'] =
        pools.status === 'ok' && (ownedPools.length > 0 || readsFirm) ? ownedPools.map(toPoolLite) : withheld(pools);

    const billingRulesOwned: FirmFacts['billingRulesOwned'] =
        billing.status === 'ok'
            ? billing.data.rules.filter((rule) => rule.firmId === id).map(toBillingRuleLite)
            : withheld(billing);

    const facts: FirmFacts = {
        firm,
        memberCount: isRestricted(memberRecords) ? memberRecords : memberRecords.length,
        accounts: accountsVisible,
        labEnrollment,
        providersOwned,
        routesOwned,
        routesGranted: routesGrantedTo(id, context),
        pools: poolsVisible,
        billingRulesOwned,
        billingRulesApplied: billingRulesAppliedTo(firm, context),
        // The admin clearing profile has no client or HTTP function in this app yet.
        clearsForFirmIds: restricted('superuser-only'),
    };
    return ready(facts, memberRecords);
}

/** Routes other firms authored that grant this firm; REST answers it only for superusers, from every route. */
function routesGrantedTo(firmId: string, { standing, superuserLists, knownFirmIds }: AssemblyContext) {
    const routes = superuserLists?.routes;
    if (!standing.isSuperuser || !routes || routes.status === 'refused') return restricted('superuser-only');
    if (routes.status === 'pending') return restricted('loading');
    if (routes.status === 'failed') return restricted('unavailable');
    return routes.data
        .filter((route) => route.firmId !== firmId && grantedFirmIds(route, knownFirmIds).includes(firmId))
        .map((route) => toRouteLite(route, knownFirmIds));
}

/**
 * Rules a parent aimed at this firm. Superusers read every rule from `GET /v1/billing`. Anyone else
 * can list a rule only through its owner firm, so the answer is complete only when each parent's
 * rules were fetched and readable; otherwise some rules are hidden and the field is restricted.
 */
function billingRulesAppliedTo(
    firm: FirmRecord,
    { standing, superuserLists, billingOwnedByFirm }: AssemblyContext,
): FirmFacts['billingRulesApplied'] {
    const aimedAtFirm = (rules: BillingRule[]) =>
        rules.filter((rule) => rule.childFirmId === firm.id).map(toBillingRuleLite);

    if (standing.isSuperuser) {
        const all = superuserLists?.billing;
        if (all?.status === 'ok') return aimedAtFirm(all.data.rules);
        if (all?.status === 'pending') return restricted('loading');
        return restricted(all?.status === 'failed' ? 'unavailable' : 'superuser-only');
    }

    const applied: BillingRule[] = [];
    for (const parent of firm.parents) {
        const owned = billingOwnedByFirm.get(parent.id);
        if (owned?.status === 'failed') return restricted('unavailable');
        if (owned?.status === 'pending') return restricted('loading');
        if (owned?.status !== 'ok') return restricted('superuser-only');
        applied.push(...owned.data.rules);
    }
    return aimedAtFirm(applied);
}
